import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/components/ui/table";
import {
  ChevronLeft,
  ChevronRight,
  Activity,
  TrendingUp,
  TrendingDown,
  Package,
  Truck,
  UserPlus,
  Settings,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getRecentStockMovements } from '@features/products/services/productService';
import { format } from "date-fns";
import { TIME_INTERVALS } from "@shared/utils/constants";

const adjustmentTypeIcons = {
  manual: Settings,
  pickup: Truck,
  package: Package,
  correction: UserPlus,
};

const adjustmentTypeColors = {
  manual: "bg-chart-1/10 text-chart-1 border-chart-1/20",
  pickup: "bg-chart-2/10 text-chart-2 border-chart-2/20",
  package: "bg-destructive/10 text-destructive border-destructive/20",
  correction: "bg-chart-5/10 text-chart-5 border-chart-5/20",
};

export function RecentStockMovements() {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    adjustmentType: "",
    limit: 20,
  });

  const {
    data: movementsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["recentStockMovements", currentPage, filters],
    queryFn: () =>
      getRecentStockMovements({
        page: currentPage,
        ...filters,
      }),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const handleFilterChange = (key, value) => {
    // For API calls, convert "all" to empty string for adjustment type filter
    const apiValue = key === "adjustmentType" && value === "all" ? "" : value;
    setFilters((prev) => ({ ...prev, [key]: apiValue }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const renderMovementBadge = (movement) => {
    const Icon = adjustmentTypeIcons[movement.adjustmentType] || Settings;
    const colorClass =
      adjustmentTypeColors[movement.adjustmentType] ||
      "bg-gray-100 text-gray-800";

    return (
      <Badge
        variant="secondary"
        className={`${colorClass} flex items-center gap-1`}
      >
        <Icon className="w-3 h-3" />
        {t(`products.adjustmentTypes.${movement.adjustmentType}`)}
      </Badge>
    );
  };

  const renderAdjustmentValue = (adjustment) => {
    const isPositive = adjustment > 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const colorClass = isPositive ? "text-chart-2" : "text-destructive";

    return (
      <div className={`flex items-center gap-1 ${colorClass} font-medium`}>
        <Icon className="w-4 h-4" />
        {isPositive ? "+" : ""}
        {adjustment}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="w-6 h-6" />
            {t("products.recentStockMovements")}
          </h2>
          <p className="text-muted-foreground">
            {t("products.recentStockMovementsDescription")}
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline">
          {t("common.refresh")}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.totalMovements")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {movementsData?.totalItems || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.positiveAdjustments")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-chart-2">
              {movementsData?.data?.filter((m) => m.adjustment > 0).length || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.negativeAdjustments")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {movementsData?.data?.filter((m) => m.adjustment < 0).length || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("products.filters")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Select
                value={filters.adjustmentType || "all"}
                onValueChange={(value) =>
                  handleFilterChange("adjustmentType", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("products.allTypes")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("products.allTypes")}</SelectItem>
                  <SelectItem value="manual">
                    {t("products.adjustmentTypes.manual")}
                  </SelectItem>
                  <SelectItem value="pickup">
                    {t("products.adjustmentTypes.pickup")}
                  </SelectItem>
                  <SelectItem value="package">
                    {t("products.adjustmentTypes.package")}
                  </SelectItem>
                  <SelectItem value="correction">
                    {t("products.adjustmentTypes.correction")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[150px]">
              <Select
                value={filters.limit.toString()}
                onValueChange={(value) =>
                  handleFilterChange("limit", parseInt(value))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">{TIME_INTERVALS.PAGINATION_OPTIONS[0].label}</SelectItem>
                  <SelectItem value="20">{TIME_INTERVALS.PAGINATION_OPTIONS[1].label}</SelectItem>
                  <SelectItem value="50">{TIME_INTERVALS.PAGINATION_OPTIONS[2].label}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Movements Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("products.movementHistory")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array(10)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="h-16 bg-muted animate-pulse rounded"
                  />
                ))}
            </div>
          ) : movementsData?.data?.length > 0 ? (
            <div className="space-y-4">
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("products.product")}</TableHead>
                      <TableHead>{t("products.date")}</TableHead>
                      <TableHead>{t("products.type")}</TableHead>
                      <TableHead>{t("products.previousStock")}</TableHead>
                      <TableHead>{t("products.adjustment")}</TableHead>
                      <TableHead>{t("products.newStock")}</TableHead>
                      <TableHead>{t("products.user")}</TableHead>
                      <TableHead>{t("products.reason")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movementsData.data.map((movement) => (
                      <TableRow key={movement._id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Package className="w-8 h-8 text-muted-foreground" />
                            <div>
                              <div className="font-medium">
                                {movement.product?.name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {t("products.currentStock")}:{" "}
                                {movement.product?.stock}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {format(
                                new Date(movement.createdAt),
                                "MMM dd, yyyy"
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {format(new Date(movement.createdAt), "HH:mm")}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{renderMovementBadge(movement)}</TableCell>
                        <TableCell className="font-medium">
                          {movement.previousStock}
                        </TableCell>
                        <TableCell>
                          {renderAdjustmentValue(movement.adjustment)}
                        </TableCell>
                        <TableCell className="font-medium">
                          {movement.newStock}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <div className="font-medium">
                                {movement.user?.name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {movement.user?.email}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{movement.reason}</div>
                            {movement.notes && (
                              <div className="text-xs text-muted-foreground">
                                {movement.notes}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {movementsData?.pageInfo &&
                movementsData.pageInfo.totalPages > 1 && (
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {t("products.showingResults", {
                        start: (currentPage - 1) * filters.limit + 1,
                        end: Math.min(
                          currentPage * filters.limit,
                          movementsData.totalItems
                        ),
                        total: movementsData.totalItems,
                      })}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={!movementsData.pageInfo.hasPrevPage}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        {t("common.previous")}
                      </Button>
                      <span className="text-sm">
                        {t("common.pageOf", {
                          current: currentPage,
                          total: movementsData.pageInfo.totalPages,
                        })}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={!movementsData.pageInfo.hasNextPage}
                      >
                        {t("common.next")}
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {t("products.noRecentMovements")}
              </h3>
              <p className="text-muted-foreground">
                {t("products.noRecentMovementsDescription")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
